import { tempo } from 'viem/chains'

import { getAllChains, getChain, SupportedChainId } from '~/constants/chains'

/** Matches a valid transaction hash: 0x followed by 64 hex characters */
const TX_HASH_REGEXP = /^0x[a-fA-F0-9]{64}$/

export enum ExplorerLinkType {
  ACCOUNT = 'account',
  TRANSACTION = 'transaction',
  TOKEN = 'token',
  USER_OP = 'userOp',
}

/**
 * Per-link-type path templates. Not part of the wire shape — the registry
 * only carries the explorer's name and base URL.
 */
const DEFAULT_PATHS = {
  accountPath: '/address',
  transactionPath: '/tx',
  tokenPath: '/token',
}

const TOKEN_PATH_OVERRIDES: Partial<Record<number, string>> = {
  [tempo.id]: '/address',
}

const JIFFYSCAN_BASE_URL = 'https://jiffyscan.xyz'

// Keep accepting links copied before Robinhood's default explorer changed.
const LEGACY_EXPLORER_CHAIN_IDS: Record<string, number> = {
  'robinhoodchain.blockscout.com': 4663,
}

interface BuildExplorerLinkParams {
  chainId: SupportedChainId
  type: ExplorerLinkType
  address?: string
  txHash?: string
  userOpHash?: string
  tokenAddress?: string
}

export function buildExplorerLink({
  chainId,
  type,
  address,
  txHash,
  userOpHash,
  tokenAddress,
}: BuildExplorerLinkParams): string | undefined {
  const explorer = getChain(chainId)?.explorer
  if (!explorer) return undefined

  const tokenPath = TOKEN_PATH_OVERRIDES[chainId] ?? DEFAULT_PATHS.tokenPath

  switch (type) {
    case ExplorerLinkType.ACCOUNT:
      if (!address) return undefined
      return `${explorer.url}${DEFAULT_PATHS.accountPath}/${address}`

    case ExplorerLinkType.TRANSACTION:
      if (!txHash) return undefined
      return `${explorer.url}${DEFAULT_PATHS.transactionPath}/${txHash}`

    case ExplorerLinkType.TOKEN:
      if (!tokenAddress) return undefined
      if (address) {
        return `${explorer.url}${tokenPath}/${tokenAddress}?a=${address}`
      }
      return `${explorer.url}${tokenPath}/${tokenAddress}`

    case ExplorerLinkType.USER_OP:
      if (!userOpHash) return undefined
      return `${explorer.url}${DEFAULT_PATHS.transactionPath}/${userOpHash}`

    default:
      return undefined
  }
}

export function buildJiffyscanLink(
  userOpHash: string,
  chainId: SupportedChainId,
): string | undefined {
  const jiffyscanName = getChain(chainId)?.jiffyscanName
  if (!jiffyscanName || !userOpHash) return undefined

  return `${JIFFYSCAN_BASE_URL}/userOpHash/${userOpHash}?network=${jiffyscanName}`
}

export function getExplorerName(chainId: SupportedChainId): string {
  return getChain(chainId)?.explorer?.name ?? 'Explorer'
}

export function getExplorerBaseUrl(
  chainId: SupportedChainId,
): string | undefined {
  return getChain(chainId)?.explorer?.url
}

/**
 * Parse an explorer link to extract the chain ID and transaction hash.
 * Supports all configured explorer URLs (etherscan, basescan, arbiscan, etc.)
 */
export function parseExplorerLink(url: string): {
  chainId: SupportedChainId
  txHash: string
} | null {
  try {
    const parsedUrl = new URL(url)
    const hostname = parsedUrl.hostname.toLowerCase()
    const pathname = parsedUrl.pathname

    // Find matching chain by explorer base URL hostname
    const matchingChain = getAllChains().find(
      (chain) =>
        chain.explorer && new URL(chain.explorer.url).hostname === hostname,
    )

    const chainId =
      matchingChain?.chainId ?? LEGACY_EXPLORER_CHAIN_IDS[hostname]

    if (chainId && getChain(chainId)) {
      // Check if it's a transaction link (contains /tx/)
      const txMatch = pathname.match(/\/tx\/(.+)/i)
      if (txMatch) {
        const txHash = txMatch[1].split('?')[0] // Remove query params if any
        if (TX_HASH_REGEXP.test(txHash)) {
          return {
            chainId,
            txHash,
          }
        }
      }
    }
  } catch {
    // Invalid URL, return null
  }
  return null
}
